import React, { useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import OrbLoader from "../../components/common/OrbLoader";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import LaunchIcon from "@mui/icons-material/Launch";
import SyncIcon from "@mui/icons-material/Sync";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import PageContainer from "../../components/layout/PageContainer";
import { HOUSE_LABEL } from "../../components/commercial-proposals/ProjectsTable";
import ProposalVersionChanges from "../../components/commercial-proposals/ProposalVersionChanges";
import {
  HOUSE_COLOR,
  HOUSE_SHORT,
} from "../../components/commercial-proposals/ProposalBoard";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { setToastMessage } from "../../store/slices/app/appSlice";
import {
  useGetCommercialProposalQuery,
  useGetContentTagsQuery,
  useRetryCommercialProposalMutation,
} from "../../store/apis/commercialProposalApi";
import { createNumberFormat } from "../../utils/numberFormat";

const BUSINESS_UNIT_LABEL: Record<string, string> = {
  BEAPOLO: "BeApolo",
  HOUSE_GROUP: "House Group",
};

const euro = createNumberFormat({
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

/**
 * Ficha de una propuesta comercial.
 *
 * Es de solo lectura a propósito: editar una propuesta emitida cambiaría el importe que
 * ya viajó al CRM y el total que alguien pudo enseñar al cliente. Lo que se hace con una
 * propuesta que cambia es emitir la versión siguiente, que es para lo que existe el V2.
 */
const ViewCommercialProposal: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const collapsed = useSelector(selectSidebarCollapsed);

  const { data, isFetching, isError } = useGetCommercialProposalQuery(
    id ?? "",
    {
      skip: !id,
    }
  );

  // El catálogo trae también los tags retirados, para poder nombrar los que lleve una
  // propuesta antigua en vez de enseñar su código.
  const { data: tagsData } = useGetContentTagsQuery();
  const tagLabels = useMemo(
    () => new Map((tagsData?.data ?? []).map((tag) => [tag.code, tag.label])),
    [tagsData]
  );
  const proposal = data?.data;

  const [retryProposal, { isLoading: isRetrying }] =
    useRetryCommercialProposalMutation();

  const handleRetry = async () => {
    if (!id) return;
    try {
      const response = await retryProposal(id).unwrap();
      const synced = response.data?.crmStatus === "SYNCED";
      dispatch(
        setToastMessage({
          message: synced
            ? t("commercialProposals.list.retryOk", "Enviado al CRM")
            : (response.data?.crmError ??
              t(
                "commercialProposals.list.retryFailed",
                "El CRM sigue sin responder"
              )),
          severity: synced ? "success" : "error",
        })
      );
    } catch {
      dispatch(
        setToastMessage({
          message: t(
            "commercialProposals.list.retryFailed",
            "El CRM sigue sin responder"
          ),
          severity: "error",
        })
      );
    }
  };

  const field = (label: string, value: React.ReactNode) => (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Box
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "64px" : "250px",
          width: { xs: "calc(100% - 64px)", sm: "auto" },
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowX: "hidden",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <PageBreadcrumbs />

        <PageContainer
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          <Button
            startIcon={<KeyboardBackspaceIcon />}
            onClick={() => navigate("/commercial-proposals")}
            sx={{ alignSelf: "flex-start" }}
          >
            {t("commercialProposals.backToList", "Volver al listado")}
          </Button>

          {isFetching && (
            <Box textAlign="center" py={6}>
              <OrbLoader
                size="large"
                label={t("common.loading", { defaultValue: "Cargando…" })}
              />
            </Box>
          )}

          {isError && (
            <Alert severity="error">
              {t(
                "commercialProposals.view.loadFailed",
                "No se pudo cargar la propuesta."
              )}
            </Alert>
          )}

          {proposal && (
            <>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 4,
                  border: `1px solid ${theme.palette.grey[200]}`,
                  p: { xs: 2, md: 3 },
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ sm: "center" }}
                  gap={2}
                >
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{ fontFamily: "monospace", fontWeight: 700 }}
                    >
                      {proposal.proposalNumber}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {proposal.name} ·{" "}
                      {BUSINESS_UNIT_LABEL[proposal.businessUnit] ??
                        proposal.businessUnit}
                    </Typography>
                  </Box>
                  <Stack direction="row" alignItems="center" gap={1}>
                    {proposal.crmAccountUnitMissing && (
                      <Tooltip
                        title={t(
                          "commercialProposals.unitMissing",
                          "Emitida sin unidad de cuenta en el CRM"
                        )}
                      >
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t(
                            "commercialProposals.unitMissingShort",
                            "sin unidad"
                          )}
                        />
                      </Tooltip>
                    )}
                    <Chip
                      size="small"
                      color={
                        proposal.crmStatus === "SYNCED"
                          ? "success"
                          : proposal.crmStatus === "ERROR"
                            ? "error"
                            : "warning"
                      }
                      label={t(
                        `commercialProposals.status.${proposal.crmStatus.toLowerCase()}`,
                        proposal.crmStatus
                      )}
                    />
                    {proposal.crmUrl && (
                      <Button
                        size="small"
                        startIcon={<LaunchIcon />}
                        component="a"
                        href={proposal.crmUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t("commercialProposals.openInCrm", "Abrir en el CRM")}
                      </Button>
                    )}
                    {proposal.crmStatus !== "SYNCED" && (
                      <Button
                        size="small"
                        startIcon={<SyncIcon />}
                        onClick={handleRetry}
                        disabled={isRetrying}
                      >
                        {t("commercialProposals.retryShort", "Reintentar")}
                      </Button>
                    )}
                    {/* La versión nueva se emite rellenando el formulario, no clonando a
                      ciegas: una versión existe porque algo ha cambiado, así que hay
                      que poder editarlo antes de guardar. */}
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      onClick={() =>
                        navigate(
                          `/commercial-proposals/new?from=${proposal.id}`
                        )
                      }
                    >
                      {t("commercialProposals.newVersion", "Nueva versión")}
                    </Button>
                  </Stack>
                </Stack>

                {!proposal.isCurrentVersion && (
                  <Alert
                    severity="info"
                    sx={{ mt: 2 }}
                    action={
                      proposal.supersededBy ? (
                        <Button
                          size="small"
                          onClick={() =>
                            navigate(
                              `/commercial-proposals/${proposal.supersededBy?.id}`
                            )
                          }
                        >
                          {t(
                            "commercialProposals.archived.open",
                            "Ver la V{{version}}",
                            { version: proposal.supersededBy.version }
                          )}
                        </Button>
                      ) : undefined
                    }
                  >
                    {/* Lo primero que hay que saber al abrirla: esta no es la que está
                      encima de la mesa. */}
                    {proposal.supersededBy
                      ? t(
                          "commercialProposals.archived.banner",
                          "Archivada: la versión vigente de este plan es {{number}}. Esta se queda como histórico y no sale en el tablero.",
                          { number: proposal.supersededBy.proposalNumber }
                        )
                      : t(
                          "commercialProposals.archived.bannerPlain",
                          "Archivada: no es la versión vigente de este plan."
                        )}
                  </Alert>
                )}

                {proposal.crmError && (
                  <Alert
                    severity={
                      proposal.crmStatus === "SYNCED" ? "warning" : "error"
                    }
                    sx={{ mt: 2 }}
                  >
                    {/* En SYNCED este campo no es un fallo: son avisos que alguien
                      debería mirar, como una unidad de cuenta ausente. */}
                    {proposal.crmError}
                  </Alert>
                )}
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 4,
                  border: `1px solid ${theme.palette.grey[200]}`,
                  p: { xs: 2, md: 3 },
                }}
              >
                <Typography variant="subtitle1" mb={2}>
                  {t(
                    "commercialProposals.view.header",
                    "Datos de la propuesta"
                  )}
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, 1fr)",
                      md: "repeat(4, 1fr)",
                    },
                    gap: 2.5,
                  }}
                >
                  {field(
                    t("commercialProposals.client", "Cliente"),
                    `${proposal.accountCode} — ${proposal.clientName}`
                  )}
                  {field(
                    t("commercialProposals.brand", "Marca (si aplica)"),
                    proposal.brandName ??
                      t("commercialProposals.noBrand", "Sin marca")
                  )}
                  {field(
                    t("commercialProposals.clientKind", "Cliente o prospect"),
                    t(
                      `commercialProposals.kind.${proposal.clientKind}`,
                      proposal.clientKind
                    )
                  )}
                  {field(
                    t(
                      "commercialProposals.clientEngagement",
                      "Tipo de cliente"
                    ),
                    t(
                      `commercialProposals.engagement.${proposal.clientEngagement}`,
                      proposal.clientEngagement
                    )
                  )}
                  {field(
                    t(
                      "commercialProposals.responsableComercial",
                      "Responsable comercial"
                    ),
                    proposal.responsableComercialName ?? "—"
                  )}
                  {field(
                    t(
                      "commercialProposals.responsablePropuesta",
                      "Responsable de propuesta"
                    ),
                    proposal.responsablePropuestaName ?? "—"
                  )}
                  {field(
                    t("commercialProposals.entryDate", "Fecha de entrada"),
                    proposal.entryDate
                  )}
                  {field(
                    t(
                      "commercialProposals.deliveryDeadline",
                      "Fecha límite de entrega"
                    ),
                    proposal.deliveryDeadline
                  )}
                  {field(
                    t(
                      "commercialProposals.activationDate",
                      "Activación estimada"
                    ),
                    proposal.estimatedActivationDate
                  )}
                  {field(
                    t("commercialProposals.durationMonths", "Duración (meses)"),
                    proposal.estimatedDurationMonths
                  )}
                  {field(
                    t("commercialProposals.view.version", "Versión"),
                    `V${proposal.version}`
                  )}
                  {field(
                    t("commercialProposals.columns.total", "Presupuesto"),
                    <strong>{euro.format(proposal.totalBudget)}</strong>
                  )}
                </Box>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 4,
                  border: `1px solid ${theme.palette.grey[200]}`,
                  p: { xs: 2, md: 3 },
                }}
              >
                <Typography variant="subtitle1" mb={2}>
                  {t("commercialProposals.new.projectsSection", "Proyectos")} (
                  {proposal.projects.length})
                </Typography>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>
                          {t(
                            "commercialProposals.projects.lineName",
                            "Nombre de línea"
                          )}
                        </TableCell>
                        {proposal.businessUnit === "HOUSE_GROUP" && (
                          <TableCell>
                            {t("commercialProposals.projects.house", "House")}
                          </TableCell>
                        )}
                        <TableCell>
                          {t(
                            "commercialProposals.projects.budgetType",
                            "Tipo de presupuesto"
                          )}
                        </TableCell>
                        <TableCell>
                          {t(
                            "commercialProposals.projects.contentTags",
                            "Contenido"
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {t(
                            "commercialProposals.projects.budget",
                            "Presupuesto"
                          )}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {proposal.projects.map((project) => (
                        <React.Fragment key={project.id}>
                          <TableRow>
                            <TableCell>{project.position}</TableCell>
                            <TableCell>{project.lineName}</TableCell>
                            {proposal.businessUnit === "HOUSE_GROUP" && (
                              <TableCell>
                                {project.house ? (
                                  <Tooltip
                                    title={
                                      HOUSE_LABEL[project.house] ??
                                      project.house
                                    }
                                  >
                                    <Chip
                                      size="small"
                                      label={
                                        HOUSE_SHORT[project.house] ??
                                        project.house
                                      }
                                      sx={{
                                        // Borde del color de marca y fondo
                                        // transparente: con los tonos claros (HoAI
                                        // menta, HoS rosa) un fondo relleno dejaria
                                        // el texto sin contraste.
                                        backgroundColor: "transparent",
                                        border: `1px solid ${HOUSE_COLOR[project.house]}`,
                                        fontWeight: 600,
                                      }}
                                    />
                                  </Tooltip>
                                ) : (
                                  "—"
                                )}
                              </TableCell>
                            )}
                            <TableCell>
                              {t(
                                `commercialProposals.budgetType.${project.budgetType}`,
                                project.budgetType
                              )}
                            </TableCell>
                            <TableCell>
                              <Stack
                                direction="row"
                                flexWrap="wrap"
                                gap={0.5}
                                useFlexGap
                              >
                                {project.contentTags.length === 0
                                  ? "—"
                                  : project.contentTags.map((tag) => (
                                      <Chip
                                        key={tag}
                                        size="small"
                                        label={tagLabels.get(tag) ?? tag}
                                      />
                                    ))}
                              </Stack>
                            </TableCell>
                            <TableCell align="right">
                              {euro.format(project.effectiveAmount)}
                            </TableCell>
                          </TableRow>

                          {project.scenarios.length > 0 && (
                            <TableRow>
                              <TableCell
                                colSpan={
                                  proposal.businessUnit === "HOUSE_GROUP"
                                    ? 6
                                    : 5
                                }
                                sx={{ pt: 0, pb: 2 }}
                              >
                                <Box pl={{ xs: 1, md: 4 }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {t(
                                      "commercialProposals.view.scenarios",
                                      "Escenarios (el de referencia es el que cuenta para el total)"
                                    )}
                                  </Typography>
                                  <Stack gap={0.5} mt={0.5}>
                                    {project.scenarios.map((scenario) => (
                                      <Stack
                                        key={scenario.id}
                                        direction="row"
                                        alignItems="center"
                                        gap={1}
                                      >
                                        <Chip
                                          size="small"
                                          variant={
                                            scenario.isReference
                                              ? "filled"
                                              : "outlined"
                                          }
                                          color={
                                            scenario.isReference
                                              ? "primary"
                                              : "default"
                                          }
                                          label={
                                            scenario.isReference
                                              ? t(
                                                  "commercialProposals.view.reference",
                                                  "referencia"
                                                )
                                              : `#${scenario.position}`
                                          }
                                        />
                                        <Typography variant="body2">
                                          {scenario.label}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          {euro.format(scenario.amount)}
                                        </Typography>
                                      </Stack>
                                    ))}
                                  </Stack>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Divider sx={{ my: 2 }} />
                <Stack direction="row" justifyContent="flex-end">
                  <Box textAlign="right">
                    <Typography variant="caption" color="text.secondary">
                      {t(
                        "commercialProposals.projects.total",
                        "Presupuesto de propuesta"
                      )}
                    </Typography>
                    <Typography variant="h6">
                      {euro.format(proposal.totalBudget)}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <ProposalVersionChanges
                proposalId={proposal.id}
                version={proposal.version}
              />
            </>
          )}
        </PageContainer>
      </Box>
    </Box>
  );
};

export default ViewCommercialProposal;
